import { useContext, useMemo } from "react";
import { Box, Grid } from "@mui/material";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SectionHeader } from "src/design";
import { useGetPublicRestaurantSchedule } from "src/data/clientBooking/queries/clientBooking";
import { useGetTableScheduleDetails } from "src/data/restaurant/queries/restaurant";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";
import {
  FeatureAccess,
  getFeatureAccess,
  isBillingInfoVisible,
  isFeatureSectionEnabled,
} from "src/helpers/FeatureCapabilityConfig";
import { DashboardHeader } from "./components/DashboardHeader";
import { SubscriptionPill } from "./components/SubscriptionPill";
import { BOOKING_FEATURES } from "src/business/bookingScope";
import { DashboardSection } from "./components/DashboardSection";
import { DashboardStatsRow } from "./components/DashboardStatsRow";
import { TodaysBookingsCard } from "./components/TodaysBookingsCard";
import { QuietSlotsCard } from "./components/QuietSlotsCard";
import { DashboardAnalyticsCard } from "./components/DashboardAnalyticsCard";
import { OffersCard } from "./components/OffersCard";
import { RestaurantInfoCard } from "./components/RestaurantInfoCard";
import { openingWindowsForDate } from "./utils/openingToday";
import { useTodaysService } from "./utils/useTodaysService";

/**
 * The restaurateur's landing page: what the subscription covers, how today's
 * service is filling up, and the state of the restaurant's offers and profile.
 *
 * Every section is gated on its own feature, and a section the viewer cannot
 * see is replaced in place — by an upgrade pitch when the subscription lacks
 * the capability, or by a "ask your administrator" note when only the
 * permission is missing. The page itself is never gated: it is where a user
 * lands, so it must always render something.
 */
const RestaurantDashboard = () => {
  const { t } = useTranslation();
  const { restaurantId = "" } = useParams<{ restaurantId: string }>();
  const { user, isLoading: userLoading } = useContext(UserContext);
  const { restaurants, isLoading: restaurantsLoading } =
    useContext(RestaurantContext);

  const restaurant = restaurants.find((entry) => entry.id === restaurantId);
  const capabilities = useMemo(
    () => restaurant?.capabilities.map((capability) => capability.name),
    [restaurant],
  );

  // The public schedule endpoint carries the opening hours and leaves without
  // requiring the restaurant-management capability, so the quiet-slot strip
  // keeps working for a viewer who only has bookings.
  const { data: details, isLoading: scheduleLoading } =
    useGetPublicRestaurantSchedule(restaurantId);

  // Every booking surface on this page is today's service, which is what the
  // same-day slice sells: either capability fetches it.
  const canSeeBookings = BOOKING_FEATURES.some(
    (feature) =>
      getFeatureAccess(feature, capabilities, user) === FeatureAccess.Granted,
  );

  // The table count is not on the public schedule payload — only the
  // permission-gated read model carries it — so the occupancy rate needs its
  // own request. It goes out under the same gate the endpoint enforces, so a
  // viewer without the table board never fires one that can only 403.
  const canReadTableSchedule = isFeatureSectionEnabled(
    ["table_schedule_read"],
    "table_scheduling",
    user,
    capabilities,
  );
  const { restaurantDetails: tableSchedule, loading: tableScheduleLoadingRaw } =
    useGetTableScheduleDetails(
      canReadTableSchedule ? restaurantId : undefined,
    );
  // The hook starts out loading and only settles once it has fetched, so a
  // request it never makes would otherwise read as loading forever.
  const tableScheduleLoading = canReadTableSchedule && tableScheduleLoadingRaw;

  const service = useTodaysService({
    restaurantId,
    schedule: details?.schedule,
    leaves: details?.leaves,
    numberOfTables: tableSchedule?.numberOfTables ?? null,
    // The table-schedule board's own fallback, so both read a booking as
    // holding its table for the same stretch.
    bookingDuration: restaurant?.bookingDuration ?? 60,
    enabled: canSeeBookings,
  });

  const openingWindows = useMemo(
    () =>
      details
        ? openingWindowsForDate(details.schedule, dayjs(), details.leaves)
        : [],
    [details],
  );

  const isClosedToday = !scheduleLoading && openingWindows.length === 0;
  const canSeeSubscription = isBillingInfoVisible(user);
  const shellLoading = userLoading || restaurantsLoading;

  return (
    <Grid container justifyContent="center">
      <Grid
        item
        xs={12}
        style={{ maxWidth: "84rem" }}
        sx={{ width: "100%", px: { xs: 1, md: 2 }, py: 1 }}
      >
        {/* Greeting + subscription */}
        <Grid
          container
          spacing={2}
          alignItems="flex-end"
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Grid item xs={12} md="auto">
            <DashboardHeader
              firstName={user?.firstName}
              restaurantName={restaurant?.name}
              bookings={
                canSeeBookings
                  ? {
                      total: service.bookings.length,
                      pending: service.pendingCount,
                    }
                  : undefined
              }
              isLoading={shellLoading || service.isLoading}
            />
          </Grid>
          {/* The plan, its price and the next billing date are organization
              business: only a viewer with the organization read permission sees
              them, so a receptionist's dashboard is just the greeting. */}
          {canSeeSubscription && (
            <Grid item xs={12} md="auto">
              <SubscriptionPill restaurantId={restaurantId} />
            </Grid>
          )}
        </Grid>

        {/* General statistics */}
        <Box sx={{ mb: 2 }}>
          <SectionHeader label={t("dashboard.sections.generalStats")} />
        </Box>
        <Box sx={{ mb: 4 }}>
          <DashboardSection
            feature={BOOKING_FEATURES}
            restaurantCapabilities={capabilities}
            restaurantId={restaurantId}
            featureLabelKey="dashboard.features.bookings"
          >
            <DashboardStatsRow
              bookingCount={service.bookings.length}
              seatCount={service.expectedSeats}
              occupancyRate={service.occupancyRate}
              bookingTrend={service.bookingTrend}
              seatTrend={service.seatTrend}
              // Occupancy needs the opening hours and the table count as
              // well as the bookings, so the row waits on all three rather
              // than flashing a dash for the tableless case.
              isLoading={
                service.isLoading || scheduleLoading || tableScheduleLoading
              }
            />
          </DashboardSection>
        </Box>

        {/* Today */}
        <Box sx={{ mb: 2 }}>
          <SectionHeader label={t("dashboard.sections.today")} />
        </Box>
        <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
          <Grid item xs={12} lg={7}>
            <DashboardSection
              feature={BOOKING_FEATURES}
              restaurantCapabilities={capabilities}
              restaurantId={restaurantId}
              featureLabelKey="dashboard.features.bookings"
            >
              <TodaysBookingsCard
                restaurantId={restaurantId}
                bookings={service.bookings}
                expectedSeats={service.expectedSeats}
                isLoading={service.isLoading}
              />
            </DashboardSection>
          </Grid>
          <Grid item xs={12} lg={5}>
            <DashboardSection
              feature={BOOKING_FEATURES}
              restaurantCapabilities={capabilities}
              restaurantId={restaurantId}
              featureLabelKey="dashboard.features.bookings"
            >
              <QuietSlotsCard
                restaurantId={restaurantId}
                hours={service.hours}
                isLoading={service.isLoading || scheduleLoading}
                isClosed={isClosedToday}
              />
            </DashboardSection>
          </Grid>
        </Grid>

        <Box sx={{ mb: 4 }}>
          <DashboardSection
            feature="analytics_dashboard"
            restaurantCapabilities={capabilities}
            restaurantId={restaurantId}
            featureLabelKey="dashboard.features.analytics"
          >
            <DashboardAnalyticsCard restaurantId={restaurantId} />
          </DashboardSection>
        </Box>

        {/* Offers & restaurant */}
        <Box sx={{ mb: 2 }}>
          <SectionHeader label={t("dashboard.sections.offersAndInfo")} />
        </Box>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <DashboardSection
              feature="deals_management"
              restaurantCapabilities={capabilities}
              restaurantId={restaurantId}
              featureLabelKey="dashboard.features.deals"
            >
              <OffersCard restaurantId={restaurantId} />
            </DashboardSection>
          </Grid>
          <Grid item xs={12} md={6}>
            <DashboardSection
              feature="restaurant_management"
              restaurantCapabilities={capabilities}
              restaurantId={restaurantId}
              featureLabelKey="dashboard.features.restaurantInfo"
            >
              {restaurant && (
                <RestaurantInfoCard
                  restaurant={restaurant}
                  openingWindows={openingWindows}
                  isLoading={scheduleLoading}
                />
              )}
            </DashboardSection>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RestaurantDashboard;
